import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveTab } from '../../redux/slices/appSlice';
import { track } from '../../utils/analytics';
import { deriveBackupHeroState, formatTimeAgo, BACKUP_TOOLS } from '../../utils/cloudBackup';

/**
 * Compact Cloud Backup pointer row for the Settings page (iOS-style:
 * "iCloud Backup · On ›"). The full surface lives on the dedicated
 * Cloud Backup page — this row just shows one line of live status and
 * navigates there.
 */
const STATE_LINES = {
    off: 'Off',
    paused_entitlement: 'Paused — GOAT required',
    backing_up: 'Backing up…',
    queued: 'Backing up…',
    attention: 'Needs attention',
    protected: 'On',
};

function BackupSettingsLink() {
    const dispatch = useDispatch();
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const activeTab = useSelector((s) => s.app?.activeTab);
    const [status, setStatus] = useState(null);

    const refresh = useCallback(() => {
        if (!backupApi) return;
        backupApi.getStatus().then((s) => { if (s && !s.error) setStatus(s); }).catch(() => {});
    }, [backupApi]);
    useEffect(() => {
        if (activeTab !== 'settings') return undefined;
        refresh();
        const t = setInterval(refresh, 30000);
        return () => clearInterval(t);
    }, [activeTab, refresh]);

    if (!backupApi) return null;

    const heroState = deriveBackupHeroState(status);
    let line = STATE_LINES[heroState] || 'Off';
    if (heroState === 'protected') {
        const last = status?.settings?.lastSnapshot || {};
        const newest = Math.max(0, ...BACKUP_TOOLS.map(({ tool }) => last[tool]?.at || 0), status?.files?.lastRun?.at || 0);
        if (newest) line = `On · ${formatTimeAgo(newest)}`;
    }

    const open = () => {
        track('backup_page_opened', { from: 'settings_row' });
        dispatch(setActiveTab('backup'));
    };

    return (
        <div style={{ marginTop: 34 }}>
            <div style={{
                fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                color: 'var(--color-text-tertiary)', marginBottom: 10,
            }}>Cloud Backup</div>
            <div
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') open(); }}
                className="bks-settings-link"
                style={{
                    border: '1px solid var(--color-border)', borderRadius: 16,
                    background: 'var(--color-panel-bg)', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    minHeight: 62, padding: '0 18px', gap: 12, cursor: 'pointer',
                }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }}>
                        <path d="M20 17.6A4.5 4.5 0 0 0 17.5 9.4a6 6 0 0 0-11.6 1.6A4 4 0 0 0 6 19h11" />
                        <path d="M12 12v6M9.5 14.5L12 12l2.5 2.5" />
                    </svg>
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <span style={{ fontSize: 15, fontWeight: 600 }}>Cloud Backup</span>
                        <span style={{ fontSize: 12, color: heroState === 'attention' ? 'var(--color-warning)' : 'var(--color-text-tertiary)' }}>
                            {line}
                        </span>
                    </span>
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-tertiary)', flexShrink: 0 }}>
                    <polyline points="9 18 15 12 9 6" />
                </svg>
            </div>
        </div>
    );
}

export default BackupSettingsLink;
